import React from 'react';

import 'swiper/css';
import 'swiper/css/effect-fade';

import { Grid2 as Grid, styled } from '@mui/material';

import ResponsiveGrid from '../../layout/grid';
import { FadeImages } from './fade-images';
import { ServiceSegment } from './service-segment';
import { TileImages } from './tile-images';
import { Columns, ServiceSegmentsTheme } from './types';

type Props<T extends React.ElementType> = {
  headline: string;
  theme?: ServiceSegmentsTheme;
  fadeDelay?: number;
  tagsComponent: T;
  columns: Columns<T>;
};

export const ServiceSegmentsOverview = <T extends React.ElementType>({
  headline,
  theme = 'dark',
  fadeDelay = 3000,
  tagsComponent,
  columns,
}: Props<T>) => {
  const getBackgroundColor = () => {
    switch (theme) {
      case 'dark':
        return '#515151';
      case 'white':
        return 'var(--color-white)';
      case 'light':
      default:
        return 'var(--color-light-grey)';
    }
  };

  return (
    <Wrapper
      sx={{
        backgroundColor: getBackgroundColor(),
      }}
      gridProps={{ justifyContent: 'center' }}
      colorTheme={theme}
    >
      <Grid size={12}>
        <Headline
          sx={{
            color: theme === 'dark' ? 'var(--color-white)' : '#000',
          }}
        >
          {headline}
        </Headline>
      </Grid>

      {columns.length > 2 ? (
        <>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12 / columns.length,
            }}
          >
            <ServiceSegment
              category={columns[0].category}
              tags={columns[0].tags}
              colorTheme={theme}
              tagsComponent={tagsComponent}
            >
              {columns[0].slides.length <= 4 ? (
                <FadeImages slides={columns[0].slides} fadeDelay={fadeDelay} />
              ) : (
                <TileImages slides={columns[0].slides} />
              )}
            </ServiceSegment>
          </Grid>

          {columns.slice(1).map((column, index) => (
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 12 / columns.length,
              }}
              key={index}
            >
              <NonClothingServiceSegment
                colorTheme={theme}
                tags={column.tags}
                category={column.category}
                tagsComponent={tagsComponent}
              >
                <FadeImages
                  slides={column.slides}
                  fadeDelay={fadeDelay * (index + 1) * 1.1}
                />
              </NonClothingServiceSegment>
            </Grid>
          ))}
        </>
      ) : (
        columns.map((column, index) => (
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
            key={index}
          >
            <ServiceSegment
              category={column.category}
              tags={column.tags}
              colorTheme={theme}
              tagsComponent={tagsComponent}
            >
              {index === columns.length - 1 || column.slides.length <= 4 ? (
                <FadeImages slides={column.slides} fadeDelay={fadeDelay} />
              ) : (
                <TileImages slides={column.slides} />
              )}
            </ServiceSegment>
          </Grid>
        ))
      )}
    </Wrapper>
  );
};

const Wrapper = styled(ResponsiveGrid)<{
  colorTheme: ServiceSegmentsTheme;
}>(({ colorTheme, theme }) => ({
  paddingBottom: colorTheme !== 'white' ? '90px' : undefined,
  paddingTop: colorTheme !== 'white' ? '50px' : undefined,

  ...(colorTheme !== 'white'
    ? {
        paddingTop: '50px',

        [theme.breakpoints.up('sm')]: {
          paddingTop: '60px',
        },

        [theme.breakpoints.up('md')]: {
          paddingTop: '70px',
        },

        [theme.breakpoints.up('lg')]: {
          paddingTop: '80px',
        },
      }
    : {}),
}));

const NonClothingServiceSegment = styled(ServiceSegment)<{
  colorTheme: ServiceSegmentsTheme;
}>(({ colorTheme }) => ({
  marginBottom: colorTheme === 'white' ? '-40px' : undefined,
})) as typeof ServiceSegment;

const Headline = styled('h2')(({ theme }) => ({
  ...theme.typography.h2,
  textAlign: 'center',

  margin: '0 0 40px',

  [theme.breakpoints.up('md')]: {
    marginBottom: '50px',
  },

  [theme.breakpoints.up('lg')]: {
    marginBottom: '60px',
  },
}));
